/**
 * apps/admin i18n — the panel's `t()`.
 *
 * The four dictionaries in `packages/i18n/locales` are CANONICAL and shared with
 * the bot and (via ARB generation) the Flutter app. This module only reads them.
 * Never inline a user-visible string in a component: `npm run i18n:parity` is the
 * gate that keeps the four key sets identical, and a literal in JSX is invisible
 * to it.
 *
 * Keys the panel uses live under `admin.*`; labels that are shared with the
 * citizen-facing channels (appeal kind, status, yettiligi role names) come from
 * `shared.*` and are deliberately NOT duplicated into `admin.*` — a handler and a
 * citizen must read the same word for the same thing.
 */
import uzLatn from '@navoiy/i18n/locales/uz-Latn.json';
import uzCyrl from '@navoiy/i18n/locales/uz-Cyrl.json';
import ru from '@navoiy/i18n/locales/ru.json';
import en from '@navoiy/i18n/locales/en.json';
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@navoiy/domain';

type Dictionary = Record<string, unknown>;

const DICTIONARIES: Readonly<Record<Locale, Dictionary>> = {
  'uz-Latn': uzLatn as Dictionary,
  'uz-Cyrl': uzCyrl as Dictionary,
  ru: ru as Dictionary,
  en: en as Dictionary,
};

const STORAGE_KEY = 'nv.admin.locale';

export function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value);
}

export function loadLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // A panel that cannot read localStorage still has to render.
  }
  return DEFAULT_LOCALE;
}

export function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Non-fatal: the choice simply does not survive a reload.
  }
}

function lookup(dictionary: Dictionary, key: string): string | undefined {
  let node: unknown = dictionary;
  for (const segment of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

export type TranslateValues = Readonly<Record<string, string | number>>;

/**
 * Resolves `key` in `locale`, falling back to uz-Latn and finally to the key
 * itself. A missing key renders as its own key rather than as an empty label —
 * a blank cell in a government panel is worse than a visible defect.
 */
export function translate(locale: Locale, key: string, values?: TranslateValues): string {
  const template =
    lookup(DICTIONARIES[locale], key) ?? lookup(DICTIONARIES[DEFAULT_LOCALE], key) ?? key;
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

export type TranslateFn = (key: string, values?: TranslateValues) => string;
